"use client";
import React, { useState, useEffect, useRef } from "react";
import { 
  ArrowRight, 
  Link, 
  Zap, 
  Hospital, 
  FirstAid, 
  Stethoscope, 
  Heartbeat, 
  Pill, 
  ShieldPlus,
  CheckCircle
} from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Link as RouterLink } from "react-router-dom";

export default function OrbitalBenefitRails({ railsData }) {
  const [hoveredId, setHoveredId] = useState(null);
  const [rotationAngle, setRotationAngle] = useState(0);
  const [autoRotate, setAutoRotate] = useState(true);
  const [windowSize, setWindowSize] = useState({
    width: typeof window !== "undefined" ? window.innerWidth : 1200,
  });

  useEffect(() => {
    const handleResize = () => setWindowSize({ width: window.innerWidth });
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isMobile = windowSize.width < 768;
  const isSmallMobile = windowSize.width < 380;
  const orbitRadius = isSmallMobile ? 120 : isMobile ? 140 : 240;

  const containerRef = useRef(null);
  const orbitRef = useRef(null);
  const nodeRefs = useRef({});

  // Auto-rotation logic
  useEffect(() => {
    let rotationTimer;
    if (autoRotate) {
      rotationTimer = setInterval(() => {
        setRotationAngle((prev) => (prev + 0.2) % 360);
      }, 50);
    }
    return () => clearInterval(rotationTimer);
  }, [autoRotate]);

  const calculateNodePosition = (index, total) => {
    const angle = ((index / total) * 360 + rotationAngle) % 360;
    const radian = (angle * Math.PI) / 180;

    const x = orbitRadius * Math.cos(radian);
    const y = orbitRadius * Math.sin(radian);

    // Z-index based on vertical position to create 3D feel
    const zIndex = Math.round(100 + 50 * Math.sin(radian));
    const baseScale = isMobile ? 0.5 : 0.8;
    const scaleFactor = isMobile ? 0.3 : 0.4;
    const scale = baseScale + scaleFactor * ((1 + Math.sin(radian)) / 2); // Perspective scaling
    const opacity = 0.5 + 0.5 * ((1 + Math.sin(radian)) / 2);

    return { x, y, angle, zIndex, scale, opacity };
  };

  const handleMouseEnter = (id) => {
    if (isMobile) return;
    setHoveredId(id);
    setAutoRotate(false);
  };

  const handleMouseLeave = () => {
    if (isMobile) return;
    setHoveredId(null);
    setAutoRotate(true);
  };

  const handleNodeClick = (id) => {
    if (hoveredId === id) {
        setHoveredId(null);
        setAutoRotate(true);
    } else {
        setHoveredId(id);
        setAutoRotate(false);
    }
  };

  return (
    <div
      className="w-full min-h-[500px] md:min-h-[700px] flex items-center justify-center relative overflow-visible"
      ref={containerRef}
      onClick={() => {
        if (isMobile && hoveredId !== null) {
            setHoveredId(null);
            setAutoRotate(true);
        }
      }}
    >
      {/* Background Decorative Rings */}
      <div 
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-[#0048ff]/5 pointer-events-none" 
        style={{ width: orbitRadius * 2.5, height: orbitRadius * 2.5 }}
      />
      <div 
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-[#0048ff]/10 animate-[pulse_4s_infinite] pointer-events-none" 
        style={{ width: orbitRadius * 2, height: orbitRadius * 2 }}
      />

      <div className="relative w-full max-w-5xl h-full flex items-center justify-center">
        <div
          className="absolute w-full h-full flex items-center justify-center"
          ref={orbitRef}
          style={{ perspective: "1200px" }}
        >
          {/* Central Core - Your Company / Governance */}
          <motion.div 
            className="absolute z-20"
            initial={{ scale: 0.9 }}
            animate={{ scale: hoveredId ? 1.05 : 1 }}
          >
            <div className="w-20 h-20 md:w-32 md:h-32 rounded-full relative flex items-center justify-center p-1 bg-white shadow-2xl shadow-[#0048ff]/20 border border-[#0048ff]/10 overflow-hidden">
               <div className="absolute inset-0 bg-gradient-to-br from-[#0048ff]/5 to-[#0048ff]/20 animate-pulse" />
               <div className="relative z-10 text-center">
                  <span className="text-[8px] md:text-[10px] font-bold text-[#0048ff]/60 uppercase tracking-widest block mb-1">Central</span>
                  <span className="text-xs md:text-sm font-bold text-[#11111f] block leading-tight">Governance<br/>Engine</span>
               </div>
               {/* Orbital Lines pointing to hovered node */}
               <AnimatePresence>
                 {hoveredId !== null && (
                   <motion.div 
                     initial={{ opacity: 0 }}
                     animate={{ opacity: 1 }}
                     exit={{ opacity: 0 }}
                     className="absolute inset-0 border-2 border-dashed border-[#0048ff]/30 rounded-full animate-[spin_10s_linear_infinite]"
                   />
                 )}
               </AnimatePresence>
            </div>
            
            {/* Specular Rim Light */}
            <div className="absolute inset-0 rounded-full border border-white/50 pointer-events-none" />
          </motion.div>

          {railsData.map((item, index) => {
            const pos = calculateNodePosition(index, railsData.length);
            const isHovered = hoveredId === item.id;
            const Icon = item.icon;

            return (
              <div
                key={item.id}
                ref={(el) => (nodeRefs.current[item.id] = el)}
                className="absolute transition-all duration-500 ease-out"
                style={{
                  transform: `translate(${pos.x}px, ${pos.y}px) scale(${isHovered ? (isMobile ? 1.1 : 1.2) : pos.scale})`,
                  zIndex: isHovered ? 300 : pos.zIndex,
                  opacity: isHovered ? 1 : pos.opacity,
                }}
                onMouseEnter={() => handleMouseEnter(item.id)}
                onMouseLeave={handleMouseLeave}
                onClick={(e) => {
                    e.stopPropagation();
                    handleNodeClick(item.id);
                }}
              >
                {/* Connection Line to Core */}
                <div 
                  className={`absolute top-1/2 left-1/2 h-px bg-gradient-to-r from-[#0048ff]/40 to-transparent origin-left transition-opacity duration-300 pointer-events-none ${isHovered ? 'opacity-100' : 'opacity-0'}`}
                  style={{ 
                    width: orbitRadius,
                    transform: `rotate(${pos.angle + 180}deg) translateX(-${orbitRadius}px)`,
                  }}
                />

                {/* Node Orb */}
                <div className={`
                  relative w-12 h-12 md:w-16 md:h-16 rounded-xl md:rounded-2xl flex items-center justify-center cursor-pointer transition-all duration-300
                  ${isHovered 
                    ? "liquid-glass bg-[#0048ff] text-white shadow-2xl shadow-[#0048ff]/30" 
                    : "bg-white text-[#0048ff] border border-[#0048ff]/10 shadow-lg"}
                `}>
                  <Icon size={isMobile ? 24 : 32} weight={isHovered ? "bold" : "light"} />
                  
                  {/* Status Ring */}
                  <div className={`absolute -inset-2 border-2 border-dashed rounded-3xl transition-all duration-700 ${isHovered ? 'border-[#0048ff]/40 rotate-180 opacity-100' : 'border-transparent opacity-0'}`} />
                </div>

                {/* Node Label (Always Visible) */}
                <div className={`mt-2 text-center transition-all ${isHovered ? 'opacity-100 scale-105' : 'opacity-80'}`}>
                   <p className={`text-[9px] md:text-xs font-bold tracking-tight ${isHovered ? 'text-[#0048ff]' : 'text-[#11111f]'}`}>
                     {item.title}
                   </p>
                </div>

                {/* Hover Detail Card */}
                <AnimatePresence>
                  {isHovered && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9, y: 10 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.9, y: 10 }}
                      className="absolute z-[400] top-16 md:top-24 left-1/2 -translate-x-1/2 w-[240px] md:w-[280px] max-w-[90vw]"
                    >
                      <Card className="liquid-glass bg-white/95 backdrop-blur-xl border-[#0048ff]/20 shadow-2xl overflow-hidden p-0">
                        <div className="h-1 w-full bg-gradient-to-r from-[#0048ff] to-cyan-400" />
                        <CardHeader className="p-4 pb-2">
                          <div className="flex justify-between items-center mb-1">
                             <Badge variant="outline" className="text-[9px] h-4 border-[#0048ff]/20 text-[#0048ff]">ACTIVE RAIL</Badge>
                             <div className="flex gap-1">
                                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                                <div className="w-1.5 h-1.5 rounded-full bg-[#0048ff]/20" />
                                <div className="w-1.5 h-1.5 rounded-full bg-[#0048ff]/20" />
                             </div>
                          </div>
                          <CardTitle className="text-base text-[#11111f] font-bold">{item.title}</CardTitle>
                        </CardHeader>
                        <CardContent className="p-4 pt-0">
                           <p className="text-xs text-[#11111f]/70 leading-relaxed mb-4">
                             {item.desc}
                           </p>
                           
                           <div className="space-y-2 mb-4">
                              {item.details.map((detail, dIdx) => (
                                <div key={dIdx} className="flex items-center gap-2 text-[10px] font-bold text-[#11111f]/80">
                                   <CheckCircle size={14} className="text-[#0048ff]" weight="bold" />
                                   {detail}
                                </div>
                              ))}
                           </div>

                           <RouterLink 
                             to={item.href}
                             className="flex items-center justify-between w-full p-2 bg-[#F8F9FB] rounded-lg group/btn"
                           >
                             <span className="text-[10px] uppercase tracking-widest font-bold text-[#11111f]/50 group-hover/btn:text-[#0048ff] transition-colors">Configure Rail</span>
                             <ArrowRight size={14} className="text-[#0048ff]" weight="bold" />
                           </RouterLink>
                        </CardContent>
                      </Card>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
